import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, Package } from 'lucide-react';
import { Button } from "@/components/ui/button";

export default function AdditiveDetailModal({ additive, products, onClose }) {
  if (!additive) return null;

  const getRiskColor = (level) => {
    switch(level) {
      case 'bajo': return { bg: 'bg-emerald-50', text: 'text-emerald-700', badge: 'bg-emerald-100', border: 'border-emerald-200' };
      case 'moderado': return { bg: 'bg-amber-50', text: 'text-amber-700', badge: 'bg-amber-100', border: 'border-amber-200' };
      case 'alto': return { bg: 'bg-red-50', text: 'text-red-700', badge: 'bg-red-100', border: 'border-red-200' };
      default: return { bg: 'bg-gray-50', text: 'text-gray-700', badge: 'bg-gray-100', border: 'border-gray-200' };
    }
  };

  const colors = getRiskColor(additive.risk_level);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[80vh] overflow-hidden"
        >
          <div className={`p-6 border-b ${colors.border} flex items-center justify-between ${colors.bg}`}>
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold ${colors.badge} ${colors.text}`}>
                {additive.code}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{additive.name}</h2>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${colors.badge} ${colors.text}`}>
                  Riesgo {additive.risk_level}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center hover:bg-white/80"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {additive.description && (
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">Descripción</h3>
                <p className="text-gray-600">{additive.description}</p>
              </div>
            )}

            {additive.health_effects && (
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  Efectos en la salud
                </h3>
                <p className="text-gray-600">{additive.health_effects}</p>
              </div>
            )}

            {additive.common_uses && (
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">Usos comunes</h3>
                <p className="text-gray-600">{additive.common_uses}</p>
              </div>
            )}

            {products && products.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Encontrado en {products.length} producto{products.length !== 1 ? 's' : ''}
                </h3>
                <div className="space-y-2">
                  {products.slice(0, 3).map((product) => (
                    <div key={product.id} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-200" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{product.name}</p>
                        {product.brand && <p className="text-sm text-gray-500">{product.brand}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}